import React, { Children, createContext, Fragment, isValidElement, useRef } from "react";
import { apolloSpaceInset1X, apolloZindexTooltip } from "@nexthink/apollo-tokens";
import useUniqueId from "../../hooks/useUniqueId";
import { StyledTable, StyledTableWrapper } from "./Table.style";
import TableColumn from "./TableColumn";
import TableRow from "./TableRow";
import TableSelectionColumn from "./TableSelectionColumn";
import TableFillerColumn from "./TableFillerColumn";
import TableGroupingColumn, { areGroupChildrenSelected, getGroupId, getGroupPaginatedProps, isFirstChildInGroup, isGroupParent, isGroupSelected, isLastChildInGroup, isParentGroupClosed, } from "./TableGroupingColumn";
import useGridNavigation from "../../hooks/useGridNavigation";
import useFocus from "./useFocus";
import TableGroupingPaginationRow from "./TableGroupingPaginationRow";
import { useUpdateSelectionOnDataChange } from "./useUpdateSelectionOnDataChange";
import { areRowsEqual, isFieldNumeric } from "./utils";
import { getSelectionString } from "../../utils/getSelectionString";
import { emptySelection } from "../../utils/emptySelection";
import { useTableDataWithHeaderRow, isHeaderRow } from "./useTableDataWithHeaderRow";
import { extractTableConfigFromDataItem, GridColumnCell } from "./GridColumnCell";
import TableActionColumn from "./TableActionColumn";
import Flex from "../box/Flex";
import { useTableSelection } from "./useTableSelection";
import { SELECTION_CELL_WIDTH } from "./TableSelectionColumn.style";
import { GROUPING_CELL_WIDTH, GROUPING_CELL_WITH_SELECTION_WIDTH } from "./TableGroupingColumn.style";
import LoadMoreButton from "./LoadMoreButton";
import TableActionBar from "./TableActionBar";
import { isTriggeredByInteractiveElement } from "../../utils/EventHelpers";
import usePortal from "../../hooks/usePortal";
const ACTION_BAR_OFFSET_X = -9;
const prefixId = "nx-table-";
const widthFitContent = "fit-content";
const rowContainerClassName = "nx-table";
const gridNavigationOptions = {
    rowsSelectorPrefix: `.${rowContainerClassName}`,
};
export const isTableColumn = (c) => c.type === TableColumn;
export const isTableActionColumn = (c) => c.type === TableActionColumn;
export const isTableSelectionColumn = (c) => c.type === TableSelectionColumn;
export const isTableGroupingColumn = (c) => c.type === TableGroupingColumn;
export const isTableFillerColumn = (c) => c.type === TableFillerColumn;
const columnProps = (id, column, tableLayout) => {
    var _a;
    let gridColumnProps = {};
    if (isTableColumn(column)) {
        gridColumnProps = Object.assign(Object.assign(Object.assign(Object.assign({ key: `${id}-${(_a = column.props) === null || _a === void 0 ? void 0 : _a.field}` }, (column.props.title && { title: column.props.title })), (column.props.field && { field: column.props.field })), (column.props.width && { width: column.props.width })), { tableLayout });
    }
    if (isTableGroupingColumn(column)) {
        const { isSelectionEnabled } = column.props;
        gridColumnProps = {
            key: `${id}-group`,
            width: `${isSelectionEnabled ? GROUPING_CELL_WITH_SELECTION_WIDTH : GROUPING_CELL_WIDTH}px`,
        };
    }
    if (isTableSelectionColumn(column)) {
        gridColumnProps = {
            key: `${id}-selection`,
            width: SELECTION_CELL_WIDTH,
        };
    }
    if (isTableActionColumn(column)) {
        gridColumnProps = Object.assign({ key: `${id}-action`, locked: true }, (tableLayout === "fixed" && column.props.kebab && { width: SELECTION_CELL_WIDTH }));
    }
    if (isTableFillerColumn(column)) {
        gridColumnProps = {
            key: `${id}-filler`,
            width: "auto",
        };
    }
    return gridColumnProps;
};
export const TableDataContext = createContext(undefined);
const HasMoreFooter = ({ hasMoreData, id, "data-insights": insightsAttr, isLoading, onNextPage, }) => {
    if (!hasMoreData) {
        return null;
    }
    return (React.createElement("div", { className: rowContainerClassName },
        React.createElement("div", { role: "row" },
            React.createElement(Flex, { py: apolloSpaceInset1X, role: "gridcell", "aria-colindex": 1, "aria-colspan": 100 },
                React.createElement(LoadMoreButton, { id: `${id}_load-more`, "data-insights": insightsAttr && `${insightsAttr}_load-more`, isLoading: isLoading, onClick: onNextPage, variant: "secondary", size: "small" })))));
};
const TableContainer = ({ id, maxWidth, dataLength, maxHeight, propperWidth, isLoading, onNextPage, hasMoreData, "data-insights": insightsAttr, children, onSelectionChange, actionBarItems, actionBarMaxActions, selected, }) => {
    const wrapperId = `${id}-wrapper`;
    const { onBlur } = useFocus(wrapperId, dataLength);
    const { gridNavigationProps } = useGridNavigation(gridNavigationOptions);
    const showActionBar = actionBarItems && selected && selected.items.length > 0;
    const { refs, portalStyles } = usePortal({
        visible: !!showActionBar,
        portalOptions: {
            flip: false,
            skidding: ACTION_BAR_OFFSET_X,
            shouldHideWhenTargetHidden: false,
        },
        placement: "top-start",
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledTableWrapper, Object.assign({ overflow: "auto", flexDirection: "column", id: wrapperId, 
            // Fix for Safari, need a width when using maxWidth
            width: propperWidth }, (maxHeight && { maxHeight }), (maxWidth && { maxWidth }), { onBlur: onBlur }, gridNavigationProps, { tabIndex: -1, ref: refs.setReference }),
            children,
            React.createElement(HasMoreFooter, { id: id, "data-insights": insightsAttr, isLoading: isLoading, onNextPage: onNextPage, hasMoreData: hasMoreData })),
        showActionBar && (React.createElement("div", { style: Object.assign(Object.assign({}, portalStyles), { zIndex: apolloZindexTooltip }), ref: refs.setFloating },
            React.createElement(TableActionBar, { id: `${id}_action-bar`, "data-insights": `${insightsAttr}_action-bar`, selected: selected, onClickClose: () => onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(Object.assign(Object.assign({}, selected), { allSelected: false, items: [] })), maxActions: actionBarMaxActions }, actionBarItems)))));
};
const Table = ({ data: initialData, onDataChange: initialOnDataChange, globalFilters, id: defaultId, "data-insights": insightsAttr, "aria-label": ariaLabel, dataItemKey, variant = "compact", sort, width = widthFitContent, maxWidth, maxHeight, children, onSortChange, selectionMode, selected = { allSelected: false, items: [] }, onSelectionChange: initialOnSelectionChange, hasMoreData, isLoading, onNextPage, grouping, onGroupingConfigChange, tableLayout = "auto", clearSelectionOnSort = true, actionBarItems, actionBarMaxActions, rowRenderer, }) => {
    var _a;
    const id = useUniqueId(prefixId, defaultId);
    const { data, onDataChange, onSelectionChange } = useTableDataWithHeaderRow(children, initialData, initialOnDataChange, initialOnSelectionChange);
    useUpdateSelectionOnDataChange({ data, grouping, selected, selectionMode, onSelectionChange, dataItemKey });
    const { handleSelection, handleHeaderSelection } = useTableSelection(data, selectionMode, selected, hasMoreData, initialData, dataItemKey, grouping, onGroupingConfigChange, onSelectionChange);
    const handleGroupExpand = (groupId) => {
        if (grouping && grouping.has(groupId)) {
            const groupConfig = grouping.get(groupId);
            if (groupConfig && onGroupingConfigChange) {
                groupConfig.expanded = !(groupConfig === null || groupConfig === void 0 ? void 0 : groupConfig.expanded);
                onGroupingConfigChange(new Map([...grouping, [groupId, groupConfig]]));
            }
        }
    };
    const getGroupRowConfig = (dataItem) => {
        return {
            isGrouping: grouping !== null,
            isGroupParent: isGroupParent(dataItem),
            isGroupSelected: grouping ? isGroupSelected(grouping, dataItem) : false,
            isGroupClosed: Boolean(grouping && isParentGroupClosed(grouping, dataItem)),
            someGroupChildrenSelected: isGroupParent(dataItem) && areGroupChildrenSelected(selected, dataItem._groupId),
            groupPaginatedProps: grouping && getGroupPaginatedProps(grouping, dataItem),
            isFirstChildInGroup: isFirstChildInGroup(data, dataItem, dataItemKey, grouping !== null),
            isLastChildInGroup: isLastChildInGroup(data, dataItem, dataItemKey, grouping !== null),
        };
    };
    const handleSortChange = (e) => {
        var _a;
        const isColCurrentlySorted = (sort === null || sort === void 0 ? void 0 : sort.field) === ((_a = e.sort) === null || _a === void 0 ? void 0 : _a[0].field);
        const nextSortCol = allColumns.find((col) => "field" in col.props && col.props.field === e.sort[0].field);
        if (!isColCurrentlySorted) {
            const initialSortDirection = nextSortCol && "initialSortDirection" in nextSortCol.props ? nextSortCol.props.initialSortDirection : undefined;
            if (initialSortDirection) {
                e.sort[0].dir = initialSortDirection;
            }
            else if (initialData && isFieldNumeric(initialData, e.sort[0].field)) {
                e.sort[0].dir = "desc";
            }
        }
        onSortChange === null || onSortChange === void 0 ? void 0 : onSortChange(Object.assign(Object.assign({}, e), { sort: e.sort[0] }));
        if (clearSelectionOnSort) {
            onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(Object.assign(Object.assign({}, selected), { allSelected: false, items: [] }));
        }
    };
    const tableActionColumns = (_a = Children.map(children, (c) => {
        if (isValidElement(c) && isTableActionColumn(c)) {
            return c;
        }
        return null;
    })) === null || _a === void 0 ? void 0 : _a.filter(Boolean);
    const allColumns = [
        ...[
            grouping && (React.createElement(TableGroupingColumn, { grouping: grouping, onExpandToggle: handleGroupExpand, isSelectionEnabled: !!selectionMode })),
        ],
        ...[
            selected && onSelectionChange && selectionMode && (React.createElement(TableSelectionColumn, { selectionMode: selectionMode, selected: selected, onRowSelection: handleSelection, onHeaderSelection: handleHeaderSelection, grouping: grouping })),
        ],
        ...(Children.map(children, (c) => {
            return c;
        }) || []),
        ...(tableLayout === "auto" ? [React.createElement(TableFillerColumn, { key: "filler-column" })] : []),
    ].filter((col) => col !== undefined);
    // ref to data to use in TableDataContext to have access to latest data in child components without having to rerender
    const tableDataRef = useRef();
    const tableConfig = {
        id,
        dataLength: (data === null || data === void 0 ? void 0 : data.length) || 0,
        variant,
        onDataChange: onDataChange || (() => undefined),
        globalFilters,
        dataItemKey,
        insightsAttr,
        sort,
        handleSortChange,
        tableActionColumns,
        columns: allColumns,
        tableLayout,
    };
    const renderRows = data === null || data === void 0 ? void 0 : data.map((dataItem, dataIndex) => {
        var _a;
        const { originalDataItem } = extractTableConfigFromDataItem(dataItem);
        const selectedIndex = (selected.items || []).findIndex((item) => areRowsEqual(item, originalDataItem, dataItemKey));
        const selectable = selectionMode === "multiple" || (selectionMode === "single" && !isGroupParent(dataItem));
        const groupingRowConfig = getGroupRowConfig(originalDataItem);
        const isHeader = isHeaderRow(originalDataItem);
        return (React.createElement(Fragment, { key: dataItemKey || dataIndex },
            (!grouping || !groupingRowConfig.isGroupClosed) && (React.createElement(TableRow, { dataItem: dataItem, key: `row-${dataIndex}`, isHeader: isHeader, "data-row": isHeader ? "head" : "body", role: "row", isSelected: selectedIndex !== -1 || groupingRowConfig.isGroupSelected || groupingRowConfig.someGroupChildrenSelected, selectable: selectable, onMouseDown: (e) => {
                    // to avoid having the text selected at the same time as rows are
                    if (e.shiftKey && selectable) {
                        emptySelection();
                    }
                }, onClick: (ev) => {
                    if (isTriggeredByInteractiveElement(ev)) {
                        // do not trigger selection if the click was on an interactive element
                        return;
                    }
                    if (!getSelectionString() && selectable && !isHeader) {
                        handleSelection(selectedIndex, originalDataItem, ev.shiftKey);
                    }
                }, isFirstChildInGroup: groupingRowConfig.isFirstChildInGroup, isLastChildInGroup: groupingRowConfig.isLastChildInGroup, groupHasMoreData: Boolean((_a = groupingRowConfig.groupPaginatedProps) === null || _a === void 0 ? void 0 : _a.hasMoreData), variant: variant, rowRenderer: rowRenderer }, allColumns.map((column, index) => {
                if (!isValidElement(column)) {
                    return null;
                }
                const columnExtraProps = columnProps(id, column, tableLayout);
                return (React.createElement(GridColumnCell, Object.assign({ key: `_${dataIndex}-${index}_cell`, id: `_${dataIndex}-${index}_cell`, dataItem: dataItem, tableConfig: tableConfig, columnIndex: index, ariaColumnIndex: index, dataIndex: dataIndex, isSelected: false, tableColumn: column, columnsCount: allColumns.length }, columnExtraProps)));
            }))),
            groupingRowConfig.isLastChildInGroup &&
                groupingRowConfig.groupPaginatedProps &&
                groupingRowConfig.groupPaginatedProps.hasMoreData &&
                !groupingRowConfig.isGroupClosed && (React.createElement(TableGroupingPaginationRow, Object.assign({ id: `${id}-group-${getGroupId(dataItem)}`, "data-insights": insightsAttr && `${insightsAttr}-group-${getGroupId(dataItem)}` }, groupingRowConfig.groupPaginatedProps)))));
    });
    tableDataRef.current = data;
    // don't display the table if no data and not loading
    if (!(data === null || data === void 0 ? void 0 : data.length) && !isLoading) {
        return null;
    }
    const propperWidth = tableLayout === "fixed" && width === widthFitContent ? "auto" : width;
    const ColumnGroup = () => (React.createElement("colgroup", null, Children.map(allColumns, (c) => (React.createElement("col", { width: columnProps(id, c, tableLayout).width })))));
    return (React.createElement(TableDataContext.Provider, { value: tableDataRef },
        React.createElement(TableContainer, { id: id, "data-insights": insightsAttr, dataLength: data === null || data === void 0 ? void 0 : data.length, isLoading: isLoading, maxHeight: maxHeight, maxWidth: maxWidth, onNextPage: onNextPage, hasMoreData: hasMoreData, propperWidth: propperWidth, selected: selected, onSelectionChange: onSelectionChange, actionBarItems: actionBarItems, actionBarMaxActions: actionBarMaxActions },
            React.createElement(StyledTable, { role: "grid", "aria-colcount": allColumns.length, "aria-rowcount": data === null || data === void 0 ? void 0 : data.length, id: id, "data-insights": insightsAttr, "aria-label": ariaLabel, tableLayout: tableLayout },
                React.createElement("table", { role: "presentation" },
                    React.createElement(ColumnGroup, null),
                    React.createElement("tbody", null, renderRows))))));
};
export default Table;
//# sourceMappingURL=Table.js.map